// Libs
import classNames from 'classnames/bind';
import moment from 'moment';
import { Field, Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/react';
import { Button, InputLabel, Typography } from '@mui/material';
import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
// Components, Layouts, Pages
// Others
import { KeyboardEnum } from '~/utils/enum/common';
import { TIME_OPTIONS } from '~/utils/mockData/common';
// Styles, images, icons
import styles from './BaseTimePicker.module.scss';
import { icons } from '~/assets';

type Props = {
  name?: string;
  label?: string;
  value?: string;
  labelOptions?: string;
  width?: number | string;
  height?: number | string;
  widthOptions?: number | string;
  errorMessage?: string;
  required?: boolean;
  disabled?: boolean;
  onChange?: (timeValue: string) => void;
};

const cx = classNames.bind(styles);

const BaseTimePicker = (props: Props) => {
  //#region Destructuring Props
  const {
    name,
    label,
    value,
    labelOptions,
    width,
    height,
    widthOptions,
    errorMessage,
    required,
    disabled = false,
    onChange,
  } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [timeValue, setTimeValue] = useState<string>('');
  const [timeDisplay, setTimeDisplay] = useState<string>('');
  const [isOpen] = useState<boolean>(false);
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    if (!value) return;

    setTimeValue(value);
    setTimeDisplay(value);
  }, [value, isOpen]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleOptionKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === KeyboardEnum.ENTER) {
      handleSaveTime();
    }
  };

  const handleSaveTime = () => {
    setTimeDisplay(timeValue);
    onChange && onChange(timeValue);
  };

  const handleCancelChooseTime = () => {
    setTimeValue(timeDisplay);
  };

  const handleTimeSelected = (value: string) => {
    setTimeValue(value);
  };

  const renderTimeDisplay = () => {
    if (!timeDisplay) {
      return t('common_select_placeholder');
    }

    return moment(timeDisplay, 'HH:mm').format('hh:mm A');
  };

  const handleTimeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setTimeValue(value);
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === KeyboardEnum.ENTER) {
      handleSaveTime();
    }
  };
  //#endregion Handle Function

  return (
    <Field id='timePickerComponent' className={cx('container')} style={{ width }}>
      {label && (
        <InputLabel required={required} className={cx('label')}>
          {label}
        </InputLabel>
      )}

      <Listbox>
        <ListboxButton
          disabled={disabled}
          className={cx('btnSelect', disabled && 'bgDisable')}
          style={{ height }}
        >
          <span className={cx('timeSelected', !timeDisplay && 'placeholder')}>{renderTimeDisplay()}</span>

          <img src={icons.commonIconArrowDown} alt={t('common_img_alt')} className={cx('timePickerIcon')} />
        </ListboxButton>

        <ListboxOptions
          className={cx('options')}
          transition
          anchor={{ to: 'bottom start', gap: '4px' }}
          style={{ width: widthOptions }}
        >
          <div className={cx('optionsHead')}>
            {labelOptions && <p className={cx('labelOptions')}>{labelOptions}</p>}

            <div className={cx('inputBox')}>
              <img src={icons.commonIconClock} alt={t('common_img_alt')} className={cx('inputClockIcon')} />

              <input
                type='time'
                name={name}
                id={name}
                className={cx('inputTime')}
                value={timeValue || timeDisplay}
                onChange={handleTimeChange}
                onKeyDown={handleInputKeyDown}
              />
            </div>
          </div>

          <div className={cx('chooseTimeBox')}>
            <p className={cx('chooseTimeLabel')}>{t('time_picker_title_choose_time')}</p>

            <div className={cx('timeOptions')}>
              {TIME_OPTIONS.map((item, index) => (
                <div
                  tabIndex={index}
                  className={cx('timeOption', timeValue === item && 'active')}
                  key={index}
                  onClick={() => handleTimeSelected(item)}
                  onKeyDown={handleOptionKeyDown}
                >
                  <span className={cx('timeOptionContent', timeValue === item && 'active')}>
                    {moment(item, 'HH:mm').format('hh:mm A')}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className={cx('timeActions')}>
            <ListboxOption value={''}>
              <Button variant='outlined' onClick={handleCancelChooseTime}>
                {t('common_action_cancel', { ns: 'common/base' })}
              </Button>
            </ListboxOption>

            <ListboxOption value={''}>
              <Button variant='contained' onClick={handleSaveTime}>
                {t('common_action_save', { ns: 'common/base' })}
              </Button>
            </ListboxOption>
          </div>
        </ListboxOptions>
      </Listbox>

      {errorMessage && <Typography className={cx('errMessage')}>{errorMessage}</Typography>}
    </Field>
  );
};

export default BaseTimePicker;
